import createApiClient from "./api";

const api = createApiClient();

export const fetchRFIs = async (
  projectId,
  page = 1,
  pageSize = 10,
  filters,
  masterSearch = "",
  sortField = null,
  sortOrder = 'asc',
  isExportPdf = false,
  archivedFilter = "active"
) => {
  const token = localStorage.getItem("token");
  const response = await api.get(`/rfis`, {
    params: {
      project_id: projectId,
      skip: (page - 1) * pageSize,
      limit: pageSize,
      filters: JSON.stringify(filters),
      master_search: masterSearch,
      sort_field: sortField,
      sort_order: sortOrder,
      is_export_pdf: isExportPdf,
      archived_filter: archivedFilter,
    },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  const { data, total } = response.data; // Assuming backend returns { data: [...rfis], total: number }
  const rfis = data.map((element) => ({
    ...element,
    formatted_assignees: element?.assignees
      ?.map((u) => {
        const name = u.username || `${u.first_name} ${u.last_name}`;
        return u.vendor_name ? `${name} (${u.vendor_name})` : name;
      })
      .join(", ") || "",
    formatted_ball_in_court_users:
      element?.ball_in_court_users
        ?.map((u) => {
          const name = u.username || `${u.first_name} ${u.last_name}`;
          return u.vendor_name ? `${name} (${u.vendor_name})` : name;
        })
        .join(", ") || "",
    formatted_distribution_list:
      element?.distribution_list_users
        ?.map((u) => {
          const name = u.username || `${u.first_name} ${u.last_name}`;
          return u.vendor_name ? `${name} (${u.vendor_name})` : name;
        })
        .join(", ") || "",
  }));

  return { rfis, total };
};

export const fetchRFIsSimple = async (projectId) => {
  const token = localStorage.getItem("token");
  const response = await api.get(`/rfis/list-rfis-basic`, {
    params: {
      project_id: projectId,
    },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return { rfis: response.data.data || [] };
};

export const fetchRFI = async (rfiId) => {
  const token = localStorage.getItem("token");
  const response = await api.get(`/rfis/${rfiId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
};

export const linkRfiToDrawing = async (rfiId, drawingId, x, y) => {
  const token = localStorage.getItem("token");
  const response = await api.patch(
    `/rfis/${rfiId}/drawing-link`,
    {
      drawing_id: drawingId,
      x,
      y,
    },
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );
  return response.data;
};

export const unlinkRfiFromDrawing = async (rfiId, drawingId, x, y) => {
  const token = localStorage.getItem("token");
  const response = await api.delete(`/rfis/${rfiId}/drawing-link`, {
    data: {
      drawing_id: drawingId,
      x,
      y,
    },
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
};

export const createRFI = async (rfiData, sendEmail = false) => {
  const token = localStorage.getItem("token");
  const response = await api.post("/rfis", rfiData, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    params: {
      email_notification: sendEmail,
    },
  });
  return response.data;
};

export const updateRFI = async (rfiId, rfiData, sendEmail = false) => {
  const token = localStorage.getItem("token");
  const response = await api.put(`/rfis/${rfiId}`, rfiData, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    params: {
      email_notification: sendEmail,
    },
  });
  return response.data;
};

export const archiveRFI = async (rfiId, archived) => {
  const token = localStorage.getItem("token");
  const response = await api.patch(
    `/rfis/${rfiId}/archive`,
    { archived },
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return response.data;
};

export const deleteRFI = async (rfiId) => {
  const token = localStorage.getItem("token");
  const response = await api.delete(`/rfis/${rfiId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
};

export const postResponse = async (rfiId, response) => {
  const token = localStorage.getItem("token");
  const payload = {
    response: response.response,
    attachments: response.attachments || [],
  };

  if (response.transfer_ball_to_rfi_manager !== undefined) {
    payload.transfer_ball_to_rfi_manager =
      response.transfer_ball_to_rfi_manager;
  }

  const result = await api.post(
    `/rfis/${rfiId}/respond`,
    payload,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return result.data;
};

export const deleteRFIResponse = async (rfiId, responseId) => {
  const token = localStorage.getItem("token");
  const response = await api.delete(`/rfis/${rfiId}/responses/${responseId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
};

export const deleteRFIResponseAttachment = async (
  rfiId,
  responseId,
  attachmentId
) => {
  const token = localStorage.getItem("token");
  const response = await api.delete(
    `/rfis/${rfiId}/responses/${responseId}/attachments/${attachmentId}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return response.data;
};

export const fetchNextRFINumber = async (projectId) => {
  const token = localStorage.getItem("token");
  const url = `/rfis/next-number?project_id=${projectId}`;

  const response = await api.get(url, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
};

export const downloadRFICoversheet = async (rfiId) => {
  const token = localStorage.getItem("token");
  const now = new Date();
  const localDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  
  const response = await api.post(`/rfis/${rfiId}/download-coversheet`, null, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    params: {
      local_date: localDate,
    },
  });
  return response.data;
};

export const getRfiDefaultNotifyRecipients = async (rfiId) => {
  const response = await api.get(`/rfis/${rfiId}/default-notify-recipients`, {
    headers: {},
  });
  return response.data?.user_ids || [];
};

export const notifyProjectUsersAboutRFI = async (rfiId, userIds = null) => {
  const requestBody = {};
  if (userIds && Array.isArray(userIds) && userIds.length > 0) {
    requestBody.user_ids = userIds;
  }

  const response = await api.post(
    `/rfis/${rfiId}/notify`,
    requestBody,
    { headers: {} }
  );
  return response.data;
};
